import { useEffect, useState } from "react";
import { ExternalLink, Calendar } from "lucide-react";

function computeRemaining(target) {
  const now = new Date().getTime();
  const t = new Date(target).getTime();
  const diff = Math.max(0, t - now);
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const m = Math.floor((diff / (1000 * 60)) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s, isPast: diff <= 0 };
}

function Cell({ value, label }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="bg-ink-700/80 backdrop-blur-sm border border-white/10 rounded-xl p-3 md:p-4 text-center shadow-inner">
        <div className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter tabular-nums">
          {String(value).padStart(2, "0")}
        </div>
        <div className="text-[9px] md:text-[10px] uppercase tracking-[0.22em] font-mono font-bold text-gcgold mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}

/**
 * Live BOE countdown to exam day + plazas convocadas.
 */
export default function CountdownBOE({ config, compact = false }) {
  const [time, setTime] = useState(() => computeRemaining(config?.exam_date));

  useEffect(() => {
    const i = setInterval(() => setTime(computeRemaining(config?.exam_date)), 1000);
    return () => clearInterval(i);
  }, [config?.exam_date]);

  if (!config) return null;

  return (
    <div className={`relative overflow-hidden ${compact ? "" : "p-6 md:p-8"} rounded-2xl border border-gcgold/30 bg-ink-800/90 backdrop-blur-md shadow-[0_0_60px_-15px_rgba(212,175,55,0.25)]`} data-testid="boe-countdown">
      <div className="absolute inset-0 bg-dot-grid-dense opacity-30 pointer-events-none" />
      <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gcgreen/15 blur-3xl" />

      <div className="relative">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gcgold opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gcgold" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono">En directo · BOE</span>
            </div>
            <div className="font-display text-lg md:text-xl font-extrabold text-white tracking-tight">
              {config.exam_label || "Examen oficial"}
            </div>
            <div className="text-xs text-slate-400 mt-0.5 font-mono">
              {new Date(config.exam_date).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}
            </div>
          </div>

          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Plazas convocadas</div>
            <div className="font-display text-3xl md:text-4xl font-extrabold text-gcgold tracking-tighter tabular-nums">
              {config.total_places?.toLocaleString("es-ES") ?? "—"}
            </div>
            <div className="text-[10px] text-slate-500 font-mono">
              {config.free_turn_places?.toLocaleString("es-ES")} turno libre
            </div>
          </div>
        </div>

        {/* Countdown grid */}
        <div className="flex gap-2 md:gap-3">
          <Cell value={time.d} label="Días" />
          <Cell value={time.h} label="Horas" />
          <Cell value={time.m} label="Min" />
          <Cell value={time.s} label="Seg" />
        </div>

        <div className="mt-4 flex items-center justify-between flex-wrap gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-slate-500 font-mono">
            <Calendar className="h-3 w-3" />
            Inscripción: {config.registration_start} → {config.registration_end}
          </div>
          {config.boe_url && (
            <a href={config.boe_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-gcgold hover:text-gcgold-light font-mono text-xs font-bold">
              {config.boe_reference} <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
